import { type FC, useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import ThemeContext from 'app/common/ThemeContext';
import { cssVariable } from 'app/utils/style';
import Tooltip from 'app/components/Tooltip';
import CircularGauge from 'app/components/Gauges/CircularGauge';
import { round } from 'app/utils/number';
import { getCDNUrl } from 'app/utils/api_url';

import style from './style.module.scss';

interface Props {
  hero: HeroWinrate;
  side: SideDota2 | null;
  orientation: Orientation;
  withTooltip: boolean;
}

const customColors = [
  { value: 75, color: '#34AD7A' },
  { value: 50, color: '#81D9B5' },
  { value: 25, color: '#FFAB00' },
  { value: 0, color: '#FF5630' }
];

const Dota2HeroPick: FC<Props> = ({ hero, side, orientation, withTooltip }) => {
  const { t } = useTranslation();
  const { theme } = useContext(ThemeContext);

  return (
    <div
      style={cssVariable('sideColor', side === null ? theme.background : theme[`vg_dota_${side}`])}
      className={classNames(style.dota2HeroPick, 'override-dota_hero_pick', {
        [style.reversed]: orientation === 'reversed'
      })}
    >
      {hero.winrate !== null ? (
        <CircularGauge
          value={round(hero.winrate * 100, 0)}
          hidePercentageSign
          customColors={customColors}
          className={classNames(style.winrateGauge, 'override-dota_hero_pick_winrate')}
        />
      ) : (
        <span className={classNames(style.notPlayed, 'override-dota_hero_pick_winrate')}>
          {t('component_hero-winrate_not-played')}
        </span>
      )}

      <Tooltip
        content={`${hero?.name}, ${t('component_hero-winrate_last-3-months')}`}
        active={!!hero && withTooltip}
      >
        {hero && (
          <span
            style={cssVariable('hero', `url(${getCDNUrl(hero.image_url)})`)}
            className={classNames(style.heroImage, 'override-dota_hero_image')}
          />
        )}
        {!hero && (
          <span className={classNames(style.heroImage, style.heroPlaceholder)}>{' / '}</span>
        )}
      </Tooltip>
    </div>
  );
};

Dota2HeroPick.displayName = 'Dota2HeroPick';

export default Dota2HeroPick;
